import { CustomDomain } from "@/lib/api/custom-domains";

/**
 * Entri DNS-record mapping utilities.
 *
 * Pure helpers that translate our stored custom-domain DNS records into the shape
 * Entri's SDK expects (`{ type, host, value, ttl }`). The SDK itself is loaded and
 * driven by the `useEntri` hook (`@/hooks/use-entri`) via the official `entrijs`
 * package — these functions stay framework-agnostic and unit-tested.
 */

export interface EntriDnsRecord {
  type: string;
  host: string;
  value: string;
  ttl: number;
}

/**
 * Derive the Entri `host` (the record name relative to the registrable domain)
 * from a full DNS record name.
 *
 * Entri expects `host` to be the subdomain label(s) under the apex domain, with
 * "@" for the apex itself. Our backend stores fully-qualified record names.
 *
 * Examples (domain = "app.example.com", apex = "example.com"):
 *   name "app.example.com"                       -> "app"
 *   name "example.com"                           -> "@"
 *   name "_cf-custom-hostname.app.example.com"   -> "_cf-custom-hostname.app"
 */
export function toEntriHost(recordName: string, domain: string): string {
  const name = recordName.replace(/\.$/, "").toLowerCase();
  const apex = registrableDomain(domain);

  if (name === apex) return "@";
  if (name.endsWith(`.${apex}`)) {
    return name.slice(0, name.length - apex.length - 1);
  }
  // Record name isn't under the apex (unexpected) — pass through unchanged.
  return recordName;
}

/**
 * Best-effort registrable (apex) domain: the last two labels of the host.
 * Sufficient for the common single-suffix TLDs we support; multi-part public
 * suffixes (e.g. co.uk) are a known limitation handled by the manual fallback.
 */
function registrableDomain(domain: string): string {
  const labels = domain.replace(/\.$/, "").toLowerCase().split(".");
  if (labels.length <= 2) return labels.join(".");
  return labels.slice(-2).join(".");
}

/** Map our stored DNS records into Entri's record shape. */
export function toEntriRecords(
  domain: string,
  records: CustomDomain["dnsRecords"]
): EntriDnsRecord[] {
  return records.map((r) => ({
    type: r.type,
    host: toEntriHost(r.name, domain),
    value: r.value,
    ttl: r.ttl,
  }));
}
